// app-pages/AdminPage/ErrorLogsPanel.tsx
"use client";
import {
  Box,
  Text,
  Select,
  HStack,
  VStack,
  Badge,
  Flex,
  Spacer,
  Button,
  Input
} from "@chakra-ui/react";
import { useGetGenerationErrors } from "queries";
import React from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import DebouncedInput from "components/InputWithDebounce";

const ErrorCategoryBadge = ({ category }: { category: string }) => {
  const colorScheme = {
    websocket: "purple",
    generation: "blue",
    authentication: "orange",
    validation: "yellow",
    payment: "green",
    system: "red",
  }[category] || "gray";

  return (
    <Badge colorScheme={colorScheme}>
      {category}
    </Badge>
  );
};

const SeverityBadge = ({ severity }: { severity: string }) => {
  const colorScheme = {
    critical: "red",
    high: "orange",
    medium: "yellow",
    low: "green",
  }[severity] || "gray";

  return (
    <Badge colorScheme={colorScheme}>
      {severity}
    </Badge>
  );
};

export const ErrorLogsPanel = () => {
  const [userEmail, setUserEmail] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [errorCategory, setErrorCategory] = React.useState("");
  const [severity, setSeverity] = React.useState("");
  const [endDate, setEndDate] = React.useState(new Date());
  const [startDate, setStartDate] = React.useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7); 
    return date;
  });

  const formatDateToUTC = (date: Date, isEndDate: boolean = false) => {
    if (isEndDate) {
      return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 23, 59, 59)).toISOString();
    }
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())).toISOString();
  };

  const { data, isLoading } = useGetGenerationErrors({
    page,
    page_size: 20,
    user_email: userEmail,
    error_category: errorCategory || undefined,
    severity: severity || undefined,
    start_date: formatDateToUTC(startDate),
    end_date: formatDateToUTC(endDate, true),
  });

  const handleEmailChange = (value: string) => {
    setUserEmail(value);
    setPage(1);
  };

  
  const truncateString = (str: string, maxLength: number = 100): string => {
    if (str && str.length > maxLength) {
      return `${str.substring(0, maxLength)}...`;
    }
    return str;
  };

  const processContext = (context: Record<string, any>): Record<string, any> => {
    const processed = { ...context };
    
    if (processed.payload?.data?.init_images) {
      processed.payload.data.init_images = processed.payload.data.init_images.map(
        (img: string) => truncateString(img)
      );
    }
    
    return processed;
  };

  const ErrorCard = ({ error }: { error: any }) => (
    <Box 
      p={4} 
      borderWidth="1px" 
      borderRadius="lg" 
      bg="whiteAlpha.50"
      _hover={{ bg: "whiteAlpha.100" }}
    >
      <VStack align="stretch" spacing={2}>
        <Flex>
          <Text fontWeight="bold">User: {error.user_email}</Text>
          <Spacer />
          <Text fontSize="sm" color="gray.400">
            {new Date(error.created_at).toLocaleString()}
          </Text>
        </Flex>
        
        <HStack>
          <ErrorCategoryBadge category={error.error_category} />
          <SeverityBadge severity={error.severity} />
          <Badge>{error.error_type}</Badge>
        </HStack>

        <Text color="red.300">{error.error_message}</Text>
        
        {Object.keys(error.context).length > 0 && (
          <Box bg="whiteAlpha.100" p={2} borderRadius="md">
            <Text fontSize="sm" fontFamily="monospace">
            {JSON.stringify(processContext(error.context), null, 2)}
            </Text>
          </Box>
        )}

        {error.credits_amount && (
          <Text fontSize="sm">
            Credits: {error.credits_amount}
            {error.was_refunded && " (Refunded)"}
          </Text>
        )}
      </VStack>
    </Box>
  );

  return (
    <VStack spacing={4} align="stretch">
      <Box>
        <HStack spacing={4} mb={4}>
          <DebouncedInput
            placeholder="Filter by user email"
            value={userEmail}
            onChange={handleEmailChange}
            delay={500}
          />
          <Select 
            placeholder="Error Category"
            value={errorCategory}
            onChange={(e) => {
              setErrorCategory(e.target.value);
              setPage(1);
            }}
          >
            <option value="websocket">Websocket</option>
            <option value="system">System</option>
          </Select>
          <Select
            placeholder="Severity"
            value={severity}
            onChange={(e) => {
              setSeverity(e.target.value);
              setPage(1);
            }}
          >
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </Select>
        </HStack>
        
        <HStack spacing={4} mb={4}>
          <Text>From:</Text>
          <DatePicker
            customInput={<Input />}
            selected={startDate}
            onChange={(date) => {
              setStartDate(date!);
              setPage(1);
            }}
          />
          <Text>To:</Text>
          <DatePicker
            customInput={<Input />}
            selected={endDate}
            onChange={(date) => {
              setEndDate(date!);
              setPage(1);
            }}
          />
        </HStack>
      </Box>

      {isLoading ? (
        <Text>Loading...</Text>
      ) : (
        <>
          <VStack spacing={4} align="stretch">
            {data?.errors.map((error:any) => (
              <ErrorCard key={error.id} error={error} />
            ))}
          </VStack>

          <HStack justify="center" spacing={4}>
            <Text>
              Page {page} of {Math.ceil((data?.total || 0) / 20)}
            </Text>
            <HStack>
              <Button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                isDisabled={page === 1}
              >
                Previous
              </Button>
              <Button
                onClick={() => setPage(p => p + 1)}
                isDisabled={!data || page >= Math.ceil(data.total / 20)}
              >
                Next
              </Button>
            </HStack>
          </HStack>
        </>
      )}
    </VStack>
  );
};

export default ErrorLogsPanel;